import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Mail, Phone, Building } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', address: '', notes: '' });

  const load = useCallback(() => {
    api.get('/customers', { params: { search } }).then(r => { setCustomers(r.data.customers || []); setTotal(r.data.total || 0); });
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => {
    setEditId(c.customer_id);
    setForm({ name: c.name, email: c.email || '', phone: c.phone || '', company: c.company || '', address: c.address || '', notes: c.notes || '' });
    setDlgOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/customers/${editId}`, form);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', form);
        toast.success('Customer created');
      }
      setDlgOpen(false); setEditId(null);
      setForm({ name: '', email: '', phone: '', company: '', address: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => { await api.delete(`/customers/${id}`); toast.success('Deleted'); load(); };

  return (
    <div data-testid="customers-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} customers</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm({ name: '', email: '', phone: '', company: '', address: '', notes: '' }); setDlgOpen(true); }} data-testid="create-customer-btn"><Plus size={16} className="mr-1" /> New Customer</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="customer-search" />
      </div>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Company</TableHead><TableHead>Created</TableHead><TableHead className="w-[50px]" />
          </TableRow></TableHeader>
          <TableBody>
            {customers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers</TableCell></TableRow>}
            {customers.map(c => (
              <TableRow key={c.customer_id} className="hover:bg-muted/50" data-testid={`customer-row-${c.customer_id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-sm"><span className="flex items-center gap-1">{c.email && <Mail size={12} className="text-muted-foreground" />}{c.email || '-'}</span></TableCell>
                <TableCell className="text-sm"><span className="flex items-center gap-1">{c.phone && <Phone size={12} className="text-muted-foreground" />}{c.phone || '-'}</span></TableCell>
                <TableCell className="text-sm"><span className="flex items-center gap-1">{c.company && <Building size={12} className="text-muted-foreground" />}{c.company || '-'}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.created_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(c)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => del(c.customer_id)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? 'Edit Customer' : 'New Customer'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="customer-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="customer-email-input" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="customer-phone-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Company</Label><Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} data-testid="customer-company-input" /></div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button><Button onClick={save} disabled={!form.name} data-testid="customer-save-btn">{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
